import { usePrevious } from '@dnd-kit/utilities';
import { faPenToSquare } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Chip, Stack, Tooltip, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-premium';
import React, { useEffect } from 'react';
import { generatePath, NavLink } from 'react-router-dom';

import { EDIT_AD_GROUP_URL } from '../../constants/routeUrls';
import useContractCampaigns from '../../hooks/useContractCampaigns';
import { useContractManagerContext } from '../../hooks/useContractManagerContext';
import oneDspTheme from '../../theme/oneDspTheme';
import { isAggregateCell } from '../../util/tableUtils';
import DataGrid from '../shared/DataGrid';

function AdGroupEditLink({ params }: { params: any }) {
  const { contract } = useContractManagerContext();
  const { contractId } = contract;

  return (
    <NavLink to={generatePath(EDIT_AD_GROUP_URL, { id: contractId.toString(), adGroupId: params.row.id.toString() })}>
      <Tooltip title="Edit Ad Group" placement="top-end">
        <FontAwesomeIcon icon={faPenToSquare} color="rgba(0, 0, 0, 0.54)" />
      </Tooltip>
    </NavLink>
  );
}

function ContractAdGroups({ setCount }: { setCount: (count: number) => void }) {
  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 10,
  });

  const {
    contract,
    selectedLineItems,
    selectedLineItemIds,
    setSelectedLineItemIds,
    selectedAdGroupIds,
    setSelectedAdGroupIds,
    setSelectedAdGroups,
    setTabIcons,
    setActiveTab,
  } = useContractManagerContext();

  const { data: rawData, isLoading } = useContractCampaigns(contract.contractId);

  const rows = rawData?.collection?.filter((row: any) => {
    if (selectedLineItemIds.length === 0) {
      return true;
    }
    return selectedLineItemIds.includes(row.insertionOrderId);
  });

  const previousRowCount = usePrevious(rows?.length);

  useEffect(() => {
    if (rows?.length !== previousRowCount) {
      setCount(rows?.length);
    }
  }, [rows?.length, setCount, previousRowCount]);

  const stringifiedSelectedAdGroupIds = JSON.stringify(
    Array.from(selectedAdGroupIds).sort((a, b) => Number(a) - Number(b))
  );
  const previousStringifiedSelectedAdGroupIds = usePrevious(stringifiedSelectedAdGroupIds);
  const isChangedSelectedAdGroupIds = stringifiedSelectedAdGroupIds !== previousStringifiedSelectedAdGroupIds;

  useEffect(() => {
    if (!isChangedSelectedAdGroupIds) {
      return;
    }

    // If the selectedAdGroupIds have changes, then set the selectedAdGroups to the corresponding rows
    const selectedAdGroups = rows?.filter((row: any) => selectedAdGroupIds.includes(row.id));
    setSelectedAdGroups(selectedAdGroups || []);
  }, [isChangedSelectedAdGroupIds, rows, selectedAdGroupIds, setSelectedAdGroups]);

  // If the selected ad groups have changed, then set the tab icons to have the names of the selected ad groups
  const selectedAdGroupIdsCount = selectedAdGroupIds.length;
  const firstSelectedAdGroupId = selectedAdGroupIds[0];

  useEffect(() => {
    if (!isChangedSelectedAdGroupIds) {
      return;
    }

    const onDelete = () => {
      setSelectedAdGroupIds([]);
      setActiveTab((previousActiveTab) => {
        if (previousActiveTab === 'Creatives') {
          return 'Ad Groups';
        }
        return previousActiveTab;
      });
    };

    if (selectedAdGroupIdsCount > 1) {
      setTabIcons((prev) => ({
        ...prev,
        Creatives: (
          <Chip
            label={`${selectedAdGroupIdsCount} Ad Groups`}
            variant="outlined"
            color="secondary"
            onDelete={onDelete}
          />
        ),
      }));
    } else if (selectedAdGroupIdsCount === 1) {
      const adGroup = rows.find((li) => li.id === firstSelectedAdGroupId);
      setTabIcons((prev) => ({
        ...prev,
        Creatives: (
          <Chip
            label={adGroup?.name}
            variant="outlined"
            color="secondary"
            onDelete={onDelete}
            sx={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis' }}
          />
        ),
      }));
    } else {
      setTabIcons((prev) => ({
        ...prev,
        Creatives: null,
      }));

      // Clear selected line items to force a refresh of the line items in case
      if (selectedLineItemIds.length > 0) {
        setSelectedLineItemIds([]);
        setTimeout(() => {
          setSelectedLineItemIds(selectedLineItemIds);
        }, 1);
      }
    }
  }, [
    setActiveTab,
    isChangedSelectedAdGroupIds,
    selectedAdGroupIdsCount,
    firstSelectedAdGroupId,
    rows,
    setTabIcons,
    setSelectedAdGroupIds,
    setSelectedLineItemIds,
    selectedLineItemIds,
  ]);

  const onClickName = (e, adGroupId: number) => {
    e.stopPropagation();
    e.preventDefault();
    setSelectedAdGroupIds([adGroupId]);

    // Need to set timeout to allow the state to update before switching tabs
    setTimeout(() => {
      setActiveTab('Creatives');
    }, 1);
  };

  const formatName = (name, lineItemId) => (
    <Stack direction="row" onClick={(e) => onClickName(e, lineItemId)} sx={{ cursor: 'pointer' }}>
      {name}
    </Stack>
  );

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Ad Group Name',
      width: 400,
      renderCell: (params) => !isAggregateCell(params) && formatName(params.value, params.row.id),
    },
    {
      headerName: 'Action',
      field: 'actions',
      renderCell: (params) => !isAggregateCell(params) && <AdGroupEditLink params={params} />,
      display: 'flex',
      flex: 1,
      type: 'actions',
    },
  ];

  return (
    <Stack spacing={2}>
      {selectedLineItems.length > 0 && (
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
          <Stack justifyContent="center" alignItems="center">
            <Typography color={oneDspTheme.palette.text.secondary}>Showing Ad Groups for:</Typography>
          </Stack>
          {selectedLineItems.map((lineItem) => (
            <Stack py={0.5}>
              <Chip
                key={lineItem.id}
                label={lineItem.name}
                variant="outlined"
                color="primary"
                onDelete={() => {
                  const newSelectedLineItemIds = selectedLineItemIds.filter((id) => id !== lineItem.id);
                  setSelectedLineItemIds(newSelectedLineItemIds);
                }}
              />
            </Stack>
          ))}
          <Button variant="text" onClick={() => setSelectedLineItemIds([])}>
            Clear Filters
          </Button>
        </Stack>
      )}
      <DataGrid
        loading={isLoading}
        rows={rows || []}
        columns={columns}
        pageSizeOptions={[5, 10, 25, 50, 100]}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        checkboxSelection
        disableRowSelectionOnClick
        rowSelectionModel={selectedAdGroupIds}
        onRowSelectionModelChange={setSelectedAdGroupIds}
        groupingColDef={{
          display: 'flex',
        }}
        isRowSelectable={(params) => !!params.row.id} // Only allow selection if the row has an id (not a group row)
        disableAggregation
      />
    </Stack>
  );
}

export default ContractAdGroups;
